// DEPENDENCIES
import { FormikProps, FormikValues } from "formik";
import * as Sentry from "@sentry/react";
import { AxiosError } from "axios";

// TYPES
import { DraftUUID, OnboardingType, Step, StepData } from "../../types";
import { AppDispatch, store } from "../../app/store";
import { InfoStepType } from "../../reducers/infoStepReducer";

// UTILS
import {
  checkIsEmailAvailable,
  contactBlacklistSupport,
  deleteDniImages,
  deleteLiveness,
  genericToastifyError,
  getIdentityResumePoint,
  getUnfinishedIdentityResumePoint,
  getInfoStepData,
  isCompanyWithoutShareholders,
  getSupportMail,
  MailLink,
  syncRemoteDraftSteps,
  WhatsappLink,
} from "../../utils";
import { showToastMessage } from "@allaria-tech/matter/ToastMessage";
import { goToStep } from "../../app/utils";
import {
  onUpdateReachedStep,
  onUpdateStep,
  onUpdateSubStep,
} from "../../reducers/onboardingDataReducer";
import {
  deleteBusinessDraft,
  deleteIndividualDraft,
} from "../../connectors/connectors";
import { getEmailVerificationHandlers } from "./emailValidationJuridicalStep";
import { renewExpiredToken } from "../shared/renewExpiredToken";
import {
  getRemoteStepData,
  getResumeStepData,
} from "../../components/TemplateInterpreter/utils";

// COMPONENTS
import InteractiveInformation from "../../components/InteractiveInformation/InteractiveInformation";

import stylesSupplementaryComponentInfoRenderer from "../../components/SupplementaryComponentInfoRenderer/SupplementaryComponentInfoRenderer.module.scss";
import ActiveAccountSummary from "../../components/ActiveAccountSummary/ActiveAccountSummary";

const infoJuridicalStep = (): Step => ({
  step: "information-step",
  component: (dispatch, state, formik) => {
    if (!state || !dispatch || !formik)
      throw new Error("Error: state, dispatch or formik not found");

    const email = formik?.values?.email;

    //Set user in Sentry
    if (email) {
      Sentry.setUser({ email });
    }

    const { draftUUID, stepData } = state.onboardingData;
    const { type: infoStep, pendingOnboardingType } = state.infoStep;

    // Continue
    const onPrimaryClick = async () => {
      try {
        if (infoStep === "BLACKLIST") {
          contactBlacklistSupport();
        } else {
          await onInfoStepPrimary(dispatch, formik, infoStep, stepData);
        }
      } catch (error) {
        console.log(error);
        showToastMessage(genericToastifyError, {
          containerId: "informationAlert",
          type: "ERROR",
        });
      }
    };

    // Restart or back
    const onSecondaryClick = async () => {
      try {
        await onInfoStepSecondary(
          dispatch,
          formik,
          infoStep,
          draftUUID,
          pendingOnboardingType,
        );
      } catch (error) {
        Sentry.captureException(error);
        showToastMessage(genericToastifyError, {
          containerId: "informationAlert",
          type: "ERROR",
        });
      }
    };

    const { alertType, primaryText, secondaryText, title, subtitle } =
      getInfoStepData({
        infoStep,
        onboardingType: "JURIDICAL",
        options: { pendingOnboardingType },
      });

    if (infoStep === "ACTIVE_ACCOUNT_SUMMARY")
      return (
        <ActiveAccountSummary
          title={title}
          subtitle={subtitle}
          primaryText={primaryText}
          values={formik.values}
          dispatch={dispatch}
          resetForm={formik.resetForm}
        />
      );

    return (
      <InteractiveInformation
        primaryButton={{ text: primaryText, onClick: onPrimaryClick }}
        secondaryButton={{ text: secondaryText, onClick: onSecondaryClick }}
        title={title}
        subtitle={subtitle}
        alertType={alertType}
      />
    );
  },
  hideLayoutButtons: true,
  hideStepper: true,
  centeredLogo: true,
  supplementaryComponentInfo: {
    show: (state) => {
      const { type } = state.infoStep;
      return type === "REGISTERED_EMAIL";
    },
    position: "bottom",
    component: () => {
      const { helpTextContainer } = stylesSupplementaryComponentInfoRenderer;
      return (
        <div className={helpTextContainer}>
          <p>
            Por cualquier duda podés comunicarte con nosotros enviándonos <br />{" "}
            un mail a <MailLink> {getSupportMail()}</MailLink> o via {""}
            <WhatsappLink>Whatsapp</WhatsappLink>
          </p>
        </div>
      );
    },
  },
});

const onInfoStepPrimary = async (
  dispatch: AppDispatch,
  formik: FormikProps<FormikValues>,
  infoStep: InfoStepType,
  stepData: StepData,
) => {
  switch (infoStep) {
    case "ACTIVE_ACCOUNT": {
      if (await renewExpiredToken({ dispatch, formik })) break;

      const resumePoint = getIdentityResumePoint(dispatch, 3);
      goToStep({
        dispatch,
        step: resumePoint.step,
        substep: resumePoint.substep ?? 1,
      });
      break;
    }
    case "ONBOARDING_IN_PROGRESS":
      dispatch({ type: "reset-store" });
      formik.resetForm();
      goToStep({ dispatch, step: 1 });
      break;
    case "PENDING_REGISTRY_REMOTE":
    case "PENDING_REGISTRY_LOCAL": {
      if (await renewExpiredToken({ dispatch, formik })) break;

      const hasRemoteDraft =
        infoStep === "PENDING_REGISTRY_LOCAL" &&
        (await syncRemoteDraftSteps((steps, remoteDraft) =>
          getEmailVerificationHandlers(
            dispatch,
            store.getState(),
            formik,
          ).completeSteps(steps, false, remoteDraft.referrer),
        ));

      const values = hasRemoteDraft
        ? { ...formik.values, ...store.getState().onboardingData.formValues }
        : formik.values;
      const identityDocument = values?.identityDocument;

      if (identityDocument) {
        await checkIsEmailAvailable({
          dispatch,
          idNumber: identityDocument,
        });
      }

      const { businessInformation } = values;
      const { hasJointSignature, corporateType } = businessInformation;
      const { reachedSteps } = stepData;

      if (infoStep === "PENDING_REGISTRY_LOCAL" && reachedSteps) {
        const latestState = store.getState();
        let { step: nextStep, substep: nextSubStep } = getResumeStepData({
          steps: latestState.activeTemplate?.steps,
          stepData,
          values,
          state: latestState,
        });

        // Signatory step
        if (nextStep === 5 && nextSubStep === 3) {
          if (hasJointSignature === "SI") {
            nextStep = 5;
            nextSubStep = 3;
          } else {
            if (isCompanyWithoutShareholders(corporateType)) {
              nextStep = 7;
            } else {
              nextStep = 6;
              nextSubStep = 1;
            }
          }
        }

        // Send to the last step even if it has been completed
        if (nextStep === 9) nextStep = 8;

        const remoteStepData =
          hasRemoteDraft &&
          getRemoteStepData({
            template: latestState.activeTemplate,
            state: latestState,
            values,
            dispatch,
          });

        if (remoteStepData) {
          goToStep({
            dispatch,
            step: remoteStepData.step,
            substep: remoteStepData.substep,
          });
        } else {
          dispatch(onUpdateStep(nextStep));
          dispatch(onUpdateSubStep(nextSubStep));
        }
      }

      if (infoStep === "PENDING_REGISTRY_REMOTE") {
        const identityResumePoint = getUnfinishedIdentityResumePoint(
          dispatch,
          3,
        );
        if (identityResumePoint) {
          goToStep({ dispatch, ...identityResumePoint });
          break;
        }

        const state = store.getState();
        const resumeStepData = getRemoteStepData({
          template: state.activeTemplate,
          state,
          values: formik.values,
          dispatch,
        });

        goToStep({
          dispatch,
          step: resumeStepData?.step ?? 1,
          substep: resumeStepData?.substep ?? 1,
        });
      }
      break;
    }
    case "REGISTERED_EMAIL": {
      goToStep({ dispatch, step: 1, substep: 1 });
      break;
    }
    case "DRAFT_NOT_FOUND":
      dispatch({ type: "reset-store" });
      break;
    case null:
      window.location.pathname = "/personal";
      break;
    default:
      break;
  }
};

const onInfoStepSecondary = async (
  dispatch: AppDispatch,
  formik: FormikProps<FormikValues>,
  infoStep: InfoStepType,
  draftUUID: DraftUUID,
  pendingOnboardingType?: OnboardingType,
) => {
  switch (infoStep) {
    case "ACTIVE_ACCOUNT":
    case "BLACKLIST":
    case "ACTIVE_ACCOUNT_SUMMARY":
      dispatch(onUpdateStep(1));
      break;
    case "PENDING_REGISTRY_REMOTE":
    case "PENDING_REGISTRY_LOCAL":
    case "ONBOARDING_IN_PROGRESS":
      {
        if (await renewExpiredToken({ dispatch, formik })) break;

        try {
          if (pendingOnboardingType === "PHYSICAL") {
            await deleteIndividualDraft({ draftUUID });
          } else {
            await deleteBusinessDraft({ draftUUID });
          }
        } catch (error) {
          if (
            !(error instanceof AxiosError) ||
            error.response?.status !== 404
          ) {
            throw error;
          }
        }

        await deleteDniImages({ uuid: draftUUID });
        await deleteLiveness({ uuid: draftUUID });

        dispatch({ type: "reset-store" });

        formik.resetForm();
        dispatch(onUpdateStep(1));
        dispatch(onUpdateReachedStep(1));
      }
      break;
    case null:
      dispatch(onUpdateStep(1));
      dispatch(onUpdateReachedStep(1));
      break;
    default:
      break;
  }
};

export default infoJuridicalStep;
